import type { ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { IoAlertCircle, IoConstruct, IoSparkles } from 'react-icons/io5';

import type { AiFeatureId, AiModelSettings, AiRunEstimate, AiRunResult, AiRunUsage } from '@mtgcraft/shared';
import { AI_FEATURES, estimateAiRun } from '@mtgcraft/shared';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';

import useAiUsageStats from '../hooks/useAiUsageStats';
import { formatElapsed } from '../hooks/useElapsedTimer';
import useIsAdmin from '../hooks/useIsAdmin';
import { describeEstimateSource, formatDuration, formatUsd } from '../lib/aiFormat';
import { AiChip, AiModelChips } from './AiChips';
import AiModelControls from './AiModelControls';

export interface AiFeatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** Supplies the default model settings and the estimate profile; admins can override settings per run. */
  feature: AiFeatureId;
  /** Tuning controls. Shown only to admins — these exist for prompt/cost tuning, not end users. */
  options?: ReactNode;
  /** Prompt text that will be sent, used for the pre-send size estimate */
  inputPreview?: string;
  maxInputChars?: number;
  isRunning: boolean;
  elapsedMs: number;
  error: string | null;
  run: AiRunResult<unknown> | null;
  /** Rendered once a run succeeds */
  result?: ReactNode;
  onRun: (settings: AiModelSettings | null) => void;
  runLabel?: string;
  /** Blocks the run and explains why, keeping the button from being a dead click */
  disabledReason?: string | null;
  size?: 'lg' | 'xl' | 'full';
}

function UsageSummary({ usage }: { usage: AiRunUsage }): ReactElement {
  return (
    <>
      <AiChip title="Input tokens">in {usage.input_tokens.toLocaleString()}</AiChip>
      {usage.cached_input_tokens > 0 && <AiChip title="Cached input tokens">cached {usage.cached_input_tokens.toLocaleString()}</AiChip>}
      <AiChip title="Output tokens">out {usage.output_tokens.toLocaleString()}</AiChip>
      {usage.reasoning_tokens > 0 && <AiChip title="Reasoning tokens">reasoning {usage.reasoning_tokens.toLocaleString()}</AiChip>}
    </>
  );
}

function EstimateLine({ estimate, showCost }: { estimate: AiRunEstimate; showCost: boolean }): ReactElement {
  return (
    <span className="text-ash text-xs">
      {estimate.duration_ms === null ? 'No timing data yet' : `Usually takes about ${formatElapsed(estimate.duration_ms)}`}
      {showCost && (
        <span title={`${estimate.input_tokens.toLocaleString()} in · ${estimate.output_tokens.toLocaleString()} out`}>
          {` · ~${formatUsd(estimate.cost_usd)} · ${describeEstimateSource(estimate)}`}
        </span>
      )}
    </span>
  );
}

/**
 * Shared shell for every AI feature modal: options, run button with a size estimate, a live timer
 * while the model works, error handling, and a run summary that stays visible afterwards.
 */
export default function AiFeatureModal({
  isOpen,
  onClose,
  title,
  description,
  feature,
  options,
  inputPreview,
  maxInputChars,
  isRunning,
  elapsedMs,
  error,
  run,
  result,
  onRun,
  runLabel = 'Run',
  disabledReason,
  size = 'lg',
}: AiFeatureModalProps): ReactElement {
  const { isAdmin } = useIsAdmin();
  const meta = AI_FEATURES[feature];
  const [settings, setSettings] = useState<AiModelSettings>(meta.defaults);
  const { data: stats = null } = useAiUsageStats(feature, isOpen);

  useEffect(() => {
    setSettings(AI_FEATURES[feature].defaults);
  }, [feature]);

  const inputChars = inputPreview?.length ?? 0;
  const estimate = useMemo(
    () => estimateAiRun({ feature: meta, settings: isAdmin ? settings : meta.defaults, inputChars, stats }),
    [meta, settings, isAdmin, inputChars, stats],
  );

  const isOverLimit = maxInputChars !== undefined && inputPreview !== undefined && inputPreview.length > maxInputChars;
  const blockedReason = isOverLimit ? 'This deck is too large to send.' : disabledReason;
  const showsResult = Boolean(result) && !isRunning;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size={size}>
      <div className="space-y-5">
        <p className="text-silver text-sm">{description}</p>

        {isAdmin ? (
          <section className="border-storm bg-void/40 space-y-3 rounded-lg border p-4">
            <h3 className="text-ash flex items-center gap-1.5 text-xs font-medium tracking-wide uppercase">
              <IoConstruct className="h-3.5 w-3.5" />
              Developer
            </h3>

            <AiModelControls value={settings} onChange={setSettings} defaults={meta.defaults} disabled={isRunning} />

            {options}

            <p className="text-ash text-xs">
              Sending roughly {estimate.input_tokens.toLocaleString()} input tokens, expecting ~{estimate.output_tokens.toLocaleString()}{' '}
              output
              {estimate.reasoning_tokens > 0 && ` (${estimate.reasoning_tokens.toLocaleString()} reasoning)`}
            </p>
          </section>
        ) : null}

        <div className="border-storm flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          {blockedReason ? (
            <span className="text-gold text-xs">{blockedReason}</span>
          ) : (
            <EstimateLine estimate={estimate} showCost={isAdmin} />
          )}

          <Button
            icon={<IoSparkles />}
            text={isRunning ? 'Working…' : showsResult ? 'Run again' : runLabel}
            onClick={() => onRun(isAdmin ? settings : null)}
            disabled={isRunning || Boolean(blockedReason)}
            title={blockedReason ?? undefined}
            color="primary"
          />
        </div>

        {isRunning && (
          <div className="border-storm bg-slate/40 flex items-center gap-3 rounded-lg border p-4">
            <Spinner size="sm" />
            <div className="text-sm">
              <p className="text-pearl">Thinking…</p>
              <p className="text-ash font-mono text-xs tabular-nums">{formatElapsed(elapsedMs)}</p>
            </div>
          </div>
        )}

        {error && !isRunning && (
          <div className="border-danger/40 bg-danger/10 flex items-start gap-3 rounded-lg border p-4">
            <IoAlertCircle className="text-danger mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-2">
              <p className="text-pearl text-sm">{error}</p>
              <Button text="Try again" onClick={() => onRun(isAdmin ? settings : null)} variant="outlined" color="secondary" size="small" />
            </div>
          </div>
        )}

        {!isRunning && result}

        {run && !isRunning && (
          <div className="border-storm flex flex-wrap items-center gap-1.5 border-t pt-4">
            <AiChip title="Time spent on the request">{formatElapsed(run.durationMs)}</AiChip>
            {isAdmin && run.estimate.duration_ms !== null && (
              <AiChip title="Estimated before the run">est {formatDuration(run.estimate.duration_ms)}</AiChip>
            )}
            {isAdmin && (
              <AiChip title={`Estimated ${describeEstimateSource(run.estimate)} → actual cost`}>
                {formatUsd(run.estimate.cost_usd)} → {formatUsd(run.costUsd)}
              </AiChip>
            )}
            {isAdmin && run.usage && <UsageSummary usage={run.usage} />}
            {isAdmin && <AiModelChips model={run.model} reasoningEffort={run.reasoningEffort} verbosity={run.verbosity} />}
          </div>
        )}
      </div>
    </Modal>
  );
}
